import type { Security } from '../../types';
import type { SortFilterItem } from '../Form/Autocomplete/types';

/**
 * Filter + sort used when Fuse is disabled.
 * Ranks by tier (exact > starts-with > label/searchSymbol contains > description-only),
 * then within a tier by recent-symbols > Rank > alphabetical.
 *
 * searchSymbol is the hyphen-stripped lowercase Symbol (e.g. "ethusd" for "ETH-USD"),
 * so both "ethusd" and "eth-usd" match ETH-USD.
 *
 * Uses per-tier buckets instead of wrapping each item in {item, tier} — at large
 * securities counts this avoids hundreds of thousands of small object allocations
 * per keystroke.
 */
export function createSortFilterOverride(recentSymbolsList: string[], minMatchCharLength = 2) {
  const recentIndexMap = new Map(
    recentSymbolsList
      .slice(0, 5)
      .reverse()
      .map((symbol, idx) => [symbol, idx] as const)
  );
  const collator = new Intl.Collator();

  const tiebreaker = (a: SortFilterItem<Security>, b: SortFilterItem<Security>): number => {
    const aRecent = recentIndexMap.get(a.item.Symbol) ?? -1;
    const bRecent = recentIndexMap.get(b.item.Symbol) ?? -1;
    if (aRecent > -1 || bRecent > -1) {
      return bRecent - aRecent;
    }
    if (a.item.Rank != null && b.item.Rank != null) {
      return a.item.Rank - b.item.Rank;
    }
    if (a.item.DisplaySymbol != null && b.item.DisplaySymbol != null) {
      return collator.compare(a.item.DisplaySymbol, b.item.DisplaySymbol);
    }
    return 0;
  };

  return (fullList: SortFilterItem<Security>[], searchText: string): SortFilterItem<Security>[] => {
    // Below minMatchCharLength skips filter/sort entirely — fullList already arrives sorted by recency/Rank from the caller.
    // Parity with Fuse's minMatchCharLength so enabling/disabling the flag doesn't change short-input behavior.
    if (searchText.length < minMatchCharLength) {
      return fullList;
    }

    const searchTextLower = searchText.toLowerCase();

    const exact: SortFilterItem<Security>[] = [];
    const startsWith: SortFilterItem<Security>[] = [];
    const contains: SortFilterItem<Security>[] = [];
    const description: SortFilterItem<Security>[] = [];

    for (const item of fullList) {
      const searchSymbol = item.item.searchSymbol ?? item.lowerLabel;
      if (searchTextLower === item.lowerLabel || searchTextLower === searchSymbol) {
        exact.push(item);
      } else if (item.lowerLabel.startsWith(searchTextLower) || searchSymbol.startsWith(searchTextLower)) {
        startsWith.push(item);
      } else if (item.lowerLabel.includes(searchTextLower) || searchSymbol.includes(searchTextLower)) {
        contains.push(item);
      } else if (item.lowerDescription.includes(searchTextLower)) {
        description.push(item);
      }
    }

    return [
      ...exact.toSorted(tiebreaker),
      ...startsWith.toSorted(tiebreaker),
      ...contains.toSorted(tiebreaker),
      ...description.toSorted(tiebreaker),
    ];
  };
}
